// Project storage utilities for multi-project architecture

export interface GlobalData {
  auth?: {
    isAuthenticated: boolean;
    sessionToken: string;
  };
  apiKey?: string;
  authToken?: string;
  lastAccessedProject?: string;
  motivationalDimensionsFile?: {
    name: string;
    content: string;
    lastModified: number;
    size: number;
  };
}

export interface ProjectData {
  [key: string]: any;
}

export interface StorageStructure {
  global: GlobalData;
  projects: {
    [projectId: string]: ProjectData;
  };
}

// Get the entire storage structure
export const getStorageStructure = (): StorageStructure => {
  const storageStr = localStorage.getItem('appData');
  if (!storageStr) {
    return {
      global: {},
      projects: {}
    };
  }
  try {
    return JSON.parse(storageStr);
  } catch (error) {
    console.error('Error parsing storage structure:', error);
    return {
      global: {},
      projects: {}
    };
  }
};

// Save the entire storage structure
export const saveStorageStructure = (data: StorageStructure): void => {
  localStorage.setItem('appData', JSON.stringify(data));
};

// Get global data
export const getGlobalData = (): GlobalData => {
  const structure = getStorageStructure();
  return structure.global || {};
};

// Save global data
export const saveGlobalData = (data: GlobalData): void => {
  const structure = getStorageStructure();
  structure.global = data;
  saveStorageStructure(structure);
};

// Get project-specific data
export const getProjectData = (projectId: string): ProjectData => {
  const structure = getStorageStructure();
  return structure.projects[projectId] || {};
};

// Save project-specific data
export const saveProjectData = (projectId: string, data: ProjectData): void => {
  const structure = getStorageStructure();
  if (!structure.projects) {
    structure.projects = {};
  }
  structure.projects[projectId] = data;
  saveStorageStructure(structure);
};

// Update last accessed project
export const updateLastAccessedProject = (projectId: string): void => {
  const global = getGlobalData();
  global.lastAccessedProject = projectId;
  saveGlobalData(global);
};

// Migrate existing data to new structure
export const migrateExistingData = (): void => {
  // Check if migration is needed
  if (localStorage.getItem('appData')) {
    return; // Already migrated
  }

  const motivationalDimensionsContent = localStorage.getItem('motivationalDimensionsContent');
  const motivationalDimensionsFileName = localStorage.getItem('motivationalDimensionsFileName');
  
  const newStructure: StorageStructure = {
    global: {
      auth: {
        isAuthenticated: !!localStorage.getItem('authToken'),
        sessionToken: localStorage.getItem('authToken') || ''
      },
      apiKey: localStorage.getItem('anthropicApiKey') || '',
      motivationalDimensionsFile: motivationalDimensionsContent && motivationalDimensionsFileName ? {
        name: motivationalDimensionsFileName,
        content: motivationalDimensionsContent,
        lastModified: Date.now(),
        size: motivationalDimensionsContent.length
      } : undefined
    },
    projects: {
      'sales-assistant': {
        content: JSON.parse(localStorage.getItem('contentUrls') || '{}'),
        ingestionPrompt: localStorage.getItem('ingestionPrompt'),
        salesPrompt: localStorage.getItem('salesPrompt'),
        motivationalDimensionsContent: localStorage.getItem('motivationalDimensionsContent'),
        motivationalDimensionsFileName: localStorage.getItem('motivationalDimensionsFileName'),
        ingestedContent: localStorage.getItem('ingestedContent'),
        claudeSession: JSON.parse(localStorage.getItem('claudeSession') || '{}'),
        messageHistory: JSON.parse(localStorage.getItem('messageHistory') || '[]')
      }
    }
  };

  // Save new structure
  saveStorageStructure(newStructure);

  // Clean up old localStorage items (optional - can keep for backward compatibility)
  // localStorage.removeItem('authToken');
  // localStorage.removeItem('anthropicApiKey');
  // etc...
};